import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { quotationSuppliersEndpoint } from 'apis/restaurant-inquiries/use-quotation-suppliers-api';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';

import type { QuotationSupplierProduct } from './types';

type TData = { data: QuotationSupplierProduct };
type TError = AxiosError;
type TVariables = { supplierId: number; productId: number; masterProductId: number };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * PUT .../quotation-suppliers/:supplierId/products/:productId/master-link.
 * Links an EXISTING (messy) supplier product to a master product — the row is
 * normalized to the master's data (old name preserved in synonyms). 409 when
 * either side is already linked elsewhere; the BE message explains which.
 */
export function useLinkQuotationProductMasterApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ supplierId, productId, masterProductId }: TVariables) => {
    try {
      const { data } = await http.put(`${quotationSuppliersEndpoint}/${supplierId}/products/${productId}/master-link`, {
        masterProductId,
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${quotationSuppliersEndpoint}/products/master-link`],
    onSuccess: () => {
      toast({ description: 'Product linked to the master product', status: 'success' });
      queryClient.invalidateQueries([quotationSuppliersEndpoint]);
    },
    ...options,
  });
}
